import type { SynthId } from '@/types'

// Placeholder previews, rendered in the browser with Web Audio.
// They are demo tones, not band recordings, and the UI labels them as placeholders.
// When real audio files exist, set `previewAudioUrl` / `sample.url` in the content file instead.

type Ctx = OfflineAudioContext

type Kit = {
    ctx: Ctx
    bus: AudioNode
    verb: AudioNode
    noise: AudioBuffer
    guitarAmp: AudioNode
    leadAmp: AudioNode
}

const SAMPLE_RATE = 44100

const midiToFreq = (midi: number): number => 440 * Math.pow(2, (midi - 69) / 12)

const distortionCurve = (amount: number): Float32Array<ArrayBuffer> => {
    const samples = 2048
    const curve = new Float32Array(samples)

    for (let i = 0; i < samples; i++) {
        const x = (i * 2) / samples - 1
        curve[i] = ((1 + amount) * x) / (1 + amount * Math.abs(x))
    }

    return curve
}

const makeNoise = (ctx: Ctx): AudioBuffer => {
    const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate)
    const data = buffer.getChannelData(0)

    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1

    return buffer
}

const makeImpulse = (ctx: Ctx, seconds: number, decay: number): AudioBuffer => {
    const length = Math.floor(ctx.sampleRate * seconds)
    const buffer = ctx.createBuffer(2, length, ctx.sampleRate)

    for (let channel = 0; channel < 2; channel++) {
        const data = buffer.getChannelData(channel)

        for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay)
    }

    return buffer
}

const envelope = (param: AudioParam, t: number, peak: number, attack: number, release: number, hold = 0): void => {
    param.setValueAtTime(0.0001, t)
    param.exponentialRampToValueAtTime(Math.max(peak, 0.0002), t + attack)
    if (hold > 0) param.setValueAtTime(Math.max(peak, 0.0002), t + attack + hold)
    param.exponentialRampToValueAtTime(0.0001, t + attack + hold + release)
}

const kick = (kit: Kit, t: number, velocity = 1): void => {
    const { ctx, bus } = kit
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = 'sine'
    osc.frequency.setValueAtTime(155, t)
    osc.frequency.exponentialRampToValueAtTime(44, t + 0.13)
    envelope(gain.gain, t, 0.95 * velocity, 0.003, 0.36)

    osc.connect(gain).connect(bus)
    osc.start(t)
    osc.stop(t + 0.42)
}

const snare = (kit: Kit, t: number, velocity = 1): void => {
    const { ctx, bus, verb, noise } = kit
    const source = ctx.createBufferSource()
    const filter = ctx.createBiquadFilter()
    const gain = ctx.createGain()

    source.buffer = noise
    filter.type = 'highpass'
    filter.frequency.value = 1300
    envelope(gain.gain, t, 0.42 * velocity, 0.002, 0.19)

    source.connect(filter).connect(gain)
    gain.connect(bus)
    gain.connect(verb)
    source.start(t)
    source.stop(t + 0.24)

    const body = ctx.createOscillator()
    const bodyGain = ctx.createGain()

    body.type = 'triangle'
    body.frequency.setValueAtTime(230, t)
    body.frequency.exponentialRampToValueAtTime(165, t + 0.08)
    envelope(bodyGain.gain, t, 0.32 * velocity, 0.002, 0.11)

    body.connect(bodyGain).connect(bus)
    body.start(t)
    body.stop(t + 0.15)
}

const hat = (kit: Kit, t: number, velocity = 1, open = false): void => {
    const { ctx, bus, noise } = kit
    const source = ctx.createBufferSource()
    const filter = ctx.createBiquadFilter()
    const gain = ctx.createGain()

    source.buffer = noise
    filter.type = 'highpass'
    filter.frequency.value = 7200
    envelope(gain.gain, t, 0.11 * velocity, 0.001, open ? 0.26 : 0.045)

    source.connect(filter).connect(gain).connect(bus)
    source.start(t, Math.random() * 0.5)
    source.stop(t + (open ? 0.3 : 0.06))
}

const crash = (kit: Kit, t: number, velocity = 1, length = 1.6): void => {
    const { ctx, bus, verb, noise } = kit
    const source = ctx.createBufferSource()
    const filter = ctx.createBiquadFilter()
    const gain = ctx.createGain()

    source.buffer = noise
    source.loop = true
    filter.type = 'highpass'
    filter.frequency.value = 4200
    envelope(gain.gain, t, 0.2 * velocity, 0.002, length)

    source.connect(filter).connect(gain)
    gain.connect(bus)
    gain.connect(verb)
    source.start(t)
    source.stop(t + length + 0.05)
}

const tom = (kit: Kit, t: number, frequency: number, velocity = 1): void => {
    const { ctx, bus } = kit
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = 'sine'
    osc.frequency.setValueAtTime(frequency, t)
    osc.frequency.exponentialRampToValueAtTime(frequency * 0.62, t + 0.24)
    envelope(gain.gain, t, 0.7 * velocity, 0.003, 0.32)

    osc.connect(gain).connect(bus)
    osc.start(t)
    osc.stop(t + 0.38)
}

const powerChord = (kit: Kit, t: number, length: number, root: number, options: { velocity?: number, mute?: boolean } = {}): void => {
    const { ctx, guitarAmp } = kit
    const velocity = options.velocity ?? 1
    const pick = ctx.createBiquadFilter()
    const gain = ctx.createGain()

    pick.type = 'lowpass'
    pick.frequency.value = options.mute ? 700 : 5200
    pick.Q.value = 0.6
    envelope(gain.gain, t, 0.5 * velocity, 0.006, options.mute ? 0.08 : 0.16, Math.max(0, length - 0.1))

    pick.connect(gain).connect(guitarAmp)

    for (const interval of [0, 7, 12]) {
        for (const detune of [-8, 8]) {
            const osc = ctx.createOscillator()

            osc.type = 'sawtooth'
            osc.frequency.value = midiToFreq(root + interval)
            osc.detune.value = detune
            osc.connect(pick)
            osc.start(t)
            osc.stop(t + length + 0.22)
        }
    }
}

const bassNote = (kit: Kit, t: number, length: number, midi: number, velocity = 1): void => {
    const { ctx, bus } = kit
    const saw = ctx.createOscillator()
    const sub = ctx.createOscillator()
    const tone = ctx.createBiquadFilter()
    const gain = ctx.createGain()

    saw.type = 'sawtooth'
    sub.type = 'sine'
    saw.frequency.value = midiToFreq(midi)
    sub.frequency.value = midiToFreq(midi)
    tone.type = 'lowpass'
    tone.frequency.setValueAtTime(1300, t)
    tone.frequency.exponentialRampToValueAtTime(420, t + Math.min(length, 0.25))
    envelope(gain.gain, t, 0.26 * velocity, 0.006, 0.08, Math.max(0, length - 0.09))

    saw.connect(tone)
    sub.connect(tone)
    tone.connect(gain).connect(bus)
    saw.start(t)
    sub.start(t)
    saw.stop(t + length + 0.12)
    sub.stop(t + length + 0.12)
}

const leadNote = (kit: Kit, t: number, length: number, midi: number, options: { bendFrom?: number, vibrato?: boolean } = {}): void => {
    const { ctx, leadAmp } = kit
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = 'sawtooth'

    if (options.bendFrom !== undefined) {
        osc.frequency.setValueAtTime(midiToFreq(options.bendFrom), t)
        osc.frequency.linearRampToValueAtTime(midiToFreq(midi), t + Math.min(0.12, length * 0.5))
    }
    else {
        osc.frequency.setValueAtTime(midiToFreq(midi), t)
    }

    if (options.vibrato) {
        const lfo = ctx.createOscillator()
        const depth = ctx.createGain()

        lfo.frequency.value = 5.6
        depth.gain.setValueAtTime(0, t)
        depth.gain.linearRampToValueAtTime(midiToFreq(midi) * 0.018, t + 0.35)
        lfo.connect(depth).connect(osc.frequency)
        lfo.start(t)
        lfo.stop(t + length + 0.2)
    }

    envelope(gain.gain, t, 0.55, 0.005, 0.12, Math.max(0, length - 0.06))

    osc.connect(gain).connect(leadAmp)
    osc.start(t)
    osc.stop(t + length + 0.2)
}

const pluck = (kit: Kit, t: number, midi: number, velocity = 1, length = 1.2): void => {
    const { ctx, bus, verb } = kit
    const osc = ctx.createOscillator()
    const tone = ctx.createBiquadFilter()
    const gain = ctx.createGain()

    osc.type = 'triangle'
    osc.frequency.value = midiToFreq(midi)
    tone.type = 'lowpass'
    tone.frequency.setValueAtTime(3200, t)
    tone.frequency.exponentialRampToValueAtTime(900, t + 0.4)
    envelope(gain.gain, t, 0.16 * velocity, 0.004, length)

    osc.connect(tone).connect(gain)
    gain.connect(bus)
    gain.connect(verb)
    osc.start(t)
    osc.stop(t + length + 0.05)
}

const backbeat = (kit: Kit, barStart: number, beat: number, options: { halfTime?: boolean, eighthHats?: boolean, pushKick?: boolean } = {}): void => {
    for (let step = 0; step < 8; step++) {
        const t = barStart + step * (beat / 2)
        if (options.eighthHats !== false) hat(kit, t, step % 2 === 0 ? 1 : 0.6)
    }

    if (options.halfTime) {
        kick(kit, barStart)
        kick(kit, barStart + beat * 1.5, 0.8)
        snare(kit, barStart + beat * 2)
        return
    }

    kick(kit, barStart)
    snare(kit, barStart + beat)
    kick(kit, barStart + beat * 2)
    if (options.pushKick) kick(kit, barStart + beat * 2.5, 0.8)
    snare(kit, barStart + beat * 3)
}

const trackHentikan = (kit: Kit): void => {
    const beat = 60 / 150
    const bar = beat * 4
    const progression = [40, 48, 43, 50]

    for (let i = 0; i < 4; i++) hat(kit, i * beat * 0.5, 0.7)
    for (let i = 0; i < 4; i++) tom(kit, i * beat * 0.5 + beat * 2, [240, 200, 160, 120][i])
    crash(kit, bar)

    for (let b = 0; b < 12; b++) {
        const start = bar + b * bar
        const root = progression[b % 4] + 12

        backbeat(kit, start, beat, { pushKick: true })

        for (let e = 0; e < 8; e++) {
            const t = start + e * (beat / 2)
            const accent = e === 0 || e === 3 || e === 6
            powerChord(kit, t, beat / 2 - 0.02, root, { mute: !accent, velocity: accent ? 1 : 0.7 })
            bassNote(kit, t, beat / 2 - 0.03, progression[b % 4], accent ? 1 : 0.8)
        }

        if (b % 4 === 3) crash(kit, start + bar, 0.8)
    }

    const end = bar * 13
    kick(kit, end)
    crash(kit, end, 1, 2.2)
    powerChord(kit, end, bar * 1.6, 52, { velocity: 1.1 })
    bassNote(kit, end, bar * 1.4, 40)
}

const trackDiPuncakDunia = (kit: Kit): void => {
    const beat = 60 / 120
    const bar = beat * 4
    const progression = [38, 45, 47, 43]

    for (let b = 0; b < 12; b++) {
        const start = b * bar
        const root = progression[b % 4]
        const lift = b >= 4

        if (b === 0) crash(kit, start, 0.7)
        if (b === 4 || b === 8) crash(kit, start)

        backbeat(kit, start, beat, { halfTime: !lift })

        if (lift) {
            for (let q = 0; q < 4; q++) powerChord(kit, start + q * beat, beat - 0.03, root + 12, { velocity: q === 0 ? 1 : 0.85 })
        }
        else {
            powerChord(kit, start, bar - 0.05, root + 12, { velocity: 0.8 })
        }

        for (let e = 0; e < 8; e++) bassNote(kit, start + e * (beat / 2), beat / 2 - 0.03, root, e % 2 === 0 ? 1 : 0.75)

        const melody = [74, 76, 78, 76]
        if (b >= 8) leadNote(kit, start + beat, beat * 2.6, melody[b % 4], { vibrato: true })
    }
}

const trackCintaPalingHebat = (kit: Kit): void => {
    const beat = 60 / 80
    const bar = beat * 4
    const chords = [
        [55, 59, 62, 67],
        [52, 55, 59, 64],
        [48, 52, 55, 60],
        [50, 54, 57, 62]
    ]
    const bassRoots = [43, 40, 36, 38]

    for (let b = 0; b < 8; b++) {
        const start = b * bar
        const chord = chords[b % 4]

        for (let e = 0; e < 8; e++) {
            const note = chord[[0, 1, 2, 3, 2, 1, 2, 3][e]]
            pluck(kit, start + e * (beat / 2), note, e === 0 ? 1 : 0.7)
        }

        if (b >= 2) {
            bassNote(kit, start, beat * 1.9, bassRoots[b % 4], 0.8)
            bassNote(kit, start + beat * 2, beat * 1.9, bassRoots[b % 4], 0.7)
            kick(kit, start, 0.7)
            kick(kit, start + beat * 2.5, 0.5)
            snare(kit, start + beat * 2, 0.55)
            for (let q = 0; q < 4; q++) hat(kit, start + q * beat, 0.6)
        }

        if (b >= 4) leadNote(kit, start + beat * 0.5, beat * 3, chord[3] + 12, { vibrato: true })
    }
}

const sampleRiff = (kit: Kit): void => {
    const beat = 60 / 150
    const eighth = beat / 2
    const riff: Array<[number, boolean]> = [
        [52, true], [52, false], [52, false], [55, true], [52, false], [57, true], [52, false], [55, true],
        [52, true], [52, false], [52, false], [50, true], [52, false], [48, true], [47, true], [47, true]
    ]

    riff.forEach(([root, accent], i) => {
        powerChord(kit, i * eighth, eighth - 0.02, root, { mute: !accent, velocity: accent ? 1 : 0.75 })
    })

    powerChord(kit, riff.length * eighth, 0.7, 52, { velocity: 1.05 })
}

const sampleFill = (kit: Kit): void => {
    const beat = 60 / 120
    const sixteenth = beat / 4

    for (let e = 0; e < 4; e++) hat(kit, e * (beat / 2), e % 2 === 0 ? 1 : 0.6)
    kick(kit, 0)
    snare(kit, beat)
    kick(kit, beat * 1.5, 0.8)

    const fill = [
        () => snare(kit, beat * 2, 0.8),
        () => snare(kit, beat * 2 + sixteenth, 0.6),
        () => tom(kit, beat * 2 + sixteenth * 2, 250),
        () => tom(kit, beat * 2 + sixteenth * 3, 250, 0.8),
        () => tom(kit, beat * 3, 190),
        () => tom(kit, beat * 3 + sixteenth, 190, 0.8),
        () => tom(kit, beat * 3 + sixteenth * 2, 140),
        () => tom(kit, beat * 3 + sixteenth * 3, 120, 0.9)
    ]

    fill.forEach(hit => hit())
    kick(kit, beat * 4)
    crash(kit, beat * 4, 1, 0.95)
}

const sampleLead = (kit: Kit): void => {
    const sixteenth = 60 / 120 / 4
    const lick: Array<[number, number, number | undefined]> = [
        [64, 1, undefined], [67, 1, undefined], [69, 1, undefined], [71, 2, 69],
        [69, 1, undefined], [67, 1, undefined], [64, 2, undefined], [67, 1, undefined],
        [69, 1, undefined], [72, 2, 71], [71, 1, undefined], [69, 1, undefined]
    ]
    let t = 0

    for (const [midi, steps, bendFrom] of lick) {
        leadNote(kit, t, steps * sixteenth - 0.01, midi, { bendFrom })
        t += steps * sixteenth
    }

    leadNote(kit, t, 4 - t - 0.3, 76, { bendFrom: 74, vibrato: true })
}

const sampleGroove = (kit: Kit): void => {
    const beat = 60 / 120
    const eighth = beat / 2
    const line = [40, 40, 52, 40, 43, 45, 47, 40, 40, 40, 52, 50, 48, 47, 45, 43]

    line.forEach((midi, i) => {
        bassNote(kit, i * eighth, eighth - 0.04, midi, i % 2 === 0 ? 1 : 0.8)
        hat(kit, i * eighth, i % 2 === 0 ? 0.5 : 0.3)
    })

    kick(kit, 0, 0.6)
    kick(kit, beat * 4, 0.6)
}

const ARRANGEMENTS: Record<SynthId, { seconds: number, build: (kit: Kit) => void, reverb: number }> = {
    'track-hentikan': { seconds: 24, build: trackHentikan, reverb: 0.16 },
    'track-di-puncak-dunia': { seconds: 24, build: trackDiPuncakDunia, reverb: 0.22 },
    'track-cinta-paling-hebat': { seconds: 24, build: trackCintaPalingHebat, reverb: 0.34 },
    'sample-riff': { seconds: 4, build: sampleRiff, reverb: 0.12 },
    'sample-fill': { seconds: 3, build: sampleFill, reverb: 0.14 },
    'sample-lead': { seconds: 4, build: sampleLead, reverb: 0.28 },
    'sample-groove': { seconds: 4, build: sampleGroove, reverb: 0.08 }
}

const makeAmp = (ctx: Ctx, bus: AudioNode, verb: AudioNode | null, options: { drive: number, curve: number, cabinet: number, level: number }): AudioNode => {
    const input = ctx.createGain()
    const shaper = ctx.createWaveShaper()
    const cabinet = ctx.createBiquadFilter()
    const presence = ctx.createBiquadFilter()
    const level = ctx.createGain()

    input.gain.value = options.drive
    shaper.curve = distortionCurve(options.curve)
    shaper.oversample = '2x'
    cabinet.type = 'lowpass'
    cabinet.frequency.value = options.cabinet
    cabinet.Q.value = 0.9
    presence.type = 'highpass'
    presence.frequency.value = 90
    level.gain.value = options.level

    input.connect(shaper).connect(presence).connect(cabinet).connect(level)
    level.connect(bus)
    if (verb) level.connect(verb)

    return input
}

export const renderPlaceholder = async (id: SynthId): Promise<AudioBuffer> => {
    const arrangement = ARRANGEMENTS[id]
    const ctx = new OfflineAudioContext(2, Math.ceil(SAMPLE_RATE * arrangement.seconds), SAMPLE_RATE)

    const bus = ctx.createGain()
    const compressor = ctx.createDynamicsCompressor()
    const master = ctx.createGain()
    const convolver = ctx.createConvolver()
    const verbGain = ctx.createGain()

    compressor.threshold.value = -16
    compressor.ratio.value = 4
    compressor.attack.value = 0.004
    compressor.release.value = 0.18
    master.gain.value = 0.8
    convolver.buffer = makeImpulse(ctx, 1.8, 2.6)
    verbGain.gain.value = arrangement.reverb

    bus.connect(compressor)
    convolver.connect(verbGain).connect(compressor)
    compressor.connect(master)
    master.connect(ctx.destination)

    const fadeStart = Math.max(0, arrangement.seconds - 0.35)
    master.gain.setValueAtTime(0.8, fadeStart)
    master.gain.linearRampToValueAtTime(0.0001, arrangement.seconds)

    arrangement.build({
        ctx,
        bus,
        verb: convolver,
        noise: makeNoise(ctx),
        guitarAmp: makeAmp(ctx, bus, null, { drive: 3.2, curve: 26, cabinet: 2900, level: 0.3 }),
        leadAmp: makeAmp(ctx, bus, convolver, { drive: 2.4, curve: 34, cabinet: 3600, level: 0.22 })
    })

    return ctx.startRendering()
}
